import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Settings, DollarSign, MapPin, Target, Calendar,
  Plus, Edit2, Trash2, Save, ArrowLeft, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAdsSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showAddPlacement, setShowAddPlacement] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/config/settings`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/config/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings saved successfully!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPlacement = async (placementData) => {
    try {
      const response = await fetch(`${API_URL}/api/ads/admin/config/placement/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(placementData)
      });

      if (response.ok) {
        toast.success('Placement added!');
        fetchSettings();
        setShowAddPlacement(false);
      }
    } catch (error) {
      toast.error('Failed to add placement');
    }
  };

  const handleRemovePlacement = async (placementId) => {
    if (!confirm(`Remove ${placementId} placement?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/ads/admin/config/placement/${placementId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Placement removed!');
        fetchSettings();
      }
    } catch (error) {
      toast.error('Failed to remove placement');
    }
  };

  const updatePlacementPrice = (placementId, newPrice) => {
    setSettings({
      ...settings,
      placements: {
        ...settings.placements,
        [placementId]: {
          ...settings.placements[placementId],
          price_per_day: parseInt(newPrice)
        }
      }
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">Loading settings...</p>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate('/admin/marketing/ads')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Settings className="h-8 w-8 text-blue-600" />
                Advertising Settings
              </h1>
              <p className="text-muted-foreground">Configure pricing, placements, and options</p>
            </div>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Placements & Pricing */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Ad Placements & Pricing
              </CardTitle>
              <Button size="sm" onClick={() => setShowAddPlacement(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Placement
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {settings && Object.entries(settings.placements).map(([id, placement]) => (
                <div key={id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 grid grid-cols-3 gap-4">
                      <div>
                        <Label>Placement Name</Label>
                        <Input
                          value={placement.name}
                          onChange={(e) => setSettings({
                            ...settings,
                            placements: {
                              ...settings.placements,
                              [id]: { ...placement, name: e.target.value }
                            }
                          })}
                        />
                      </div>
                      <div>
                        <Label>Price per Day (MTR tokens)</Label>
                        <Input
                          type="number"
                          value={placement.price_per_day}
                          onChange={(e) => updatePlacementPrice(id, e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={placement.description}
                          onChange={(e) => setSettings({
                            ...settings,
                            placements: {
                              ...settings.placements,
                              [id]: { ...placement, description: e.target.value }
                            }
                          })}
                        />
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="ml-2"
                      onClick={() => handleRemovePlacement(id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Badge variant="outline">ID: {id}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Call-to-Action Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Call-to-Action Options
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Available CTA Buttons</Label>
              <Textarea
                value={settings?.cta_options?.join('\n') || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  cta_options: e.target.value.split('\n').filter(s => s.trim())
                })}
                rows={6}
                placeholder="One CTA per line"
              />
              <p className="text-xs text-muted-foreground">
                Enter one option per line (e.g., Learn More, Shop Now, Visit Website)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Targeting Options */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Target Countries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings?.target_countries?.join('\n') || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  target_countries: e.target.value.split('\n').filter(s => s.trim())
                })}
                rows={6}
                placeholder="One country per line"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Target Audiences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings?.target_audiences?.join('\n') || ''}
                onChange={(e) => setSettings({
                  ...settings,
                  target_audiences: e.target.value.split('\n').filter(s => s.trim())
                })}
                rows={6}
                placeholder="One audience per line"
              />
            </CardContent>
          </Card>
        </div>

        {/* Campaign Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Campaign Limits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Maximum Campaign Duration (days)</Label>
                <Input
                  type="number"
                  value={settings?.max_duration_days || 90}
                  onChange={(e) => setSettings({
                    ...settings,
                    max_duration_days: parseInt(e.target.value)
                  })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} disabled={saving} size="lg">
            <Save className="h-5 w-5 mr-2" />
            {saving ? 'Saving Changes...' : 'Save All Changes'}
          </Button>
        </div>

        {/* Add Placement Modal */}
        {showAddPlacement && (
          <AddPlacementModal
            onClose={() => setShowAddPlacement(false)}
            onAdd={handleAddPlacement}
          />
        )}
      </div>
    </AdminLayout>
  );
};

// Add Placement Modal
const AddPlacementModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    price_per_day: 5000,
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-lg w-full">
        <CardHeader>
          <CardTitle>Add New Placement</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Placement ID *</Label>
              <Input
                value={formData.id}
                onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                placeholder="e.g., dashboard, news_feed"
                required
              />
              <p className="text-xs text-muted-foreground">Use lowercase with underscores (e.g., news_feed)</p>
            </div>

            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., News Feed"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Price per Day (MTR tokens) *</Label>
              <Input
                type="number"
                value={formData.price_per_day}
                onChange={(e) => setFormData({ ...formData, price_per_day: parseInt(e.target.value) })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this placement"
                rows={3}
                required
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                <Plus className="h-4 w-4 mr-1" />
                Add Placement
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
